/**
 * <reveal-on-scroll> wraps a media + content pair (e.g. the media-with-content
 * section) and animates each side in from its own edge the first time it
 * scrolls into view, mirroring a common "editorial" reveal pattern:
 * https://www.danielcastle.ro
 *
 * - Every target animates in the first time it crosses the intersection
 *   threshold, including one that's already on screen when observed (its
 *   reveal just plays immediately) — this stays reliable regardless of
 *   scroll position, e.g. across theme-dev hot reloads.
 * - Each element animates once, then stops being observed.
 * - Respects prefers-reduced-motion.
 */

const OBSERVER_OPTIONS = {
  // Fire as soon as the element starts entering the viewport (rather than
  // waiting for 15% of it to be visible) — with near-full-viewport-tall
  // cards, waiting for a percentage meant scrolling well past the empty,
  // still-hidden element before its reveal ever kicked in.
  threshold: 0,
  rootMargin: '0px 0px 200px 0px',
};

const STAGGER_MS = 100;

class RevealOnScroll extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    if (this.dataset.animate === 'false') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const mediaRight = this.classList.contains('media-with-content--media-right');
    const media = this.querySelector('.media-block');
    const content = this.querySelector('.media-with-content__content');

    /** @type {HTMLElement[]} */
    const targets = [];

    if (media instanceof HTMLElement) {
      media.dataset.revealDirection = mediaRight ? 'right' : 'left';
      targets.push(media);
    }

    if (content instanceof HTMLElement) {
      content.dataset.revealDirection = mediaRight ? 'left' : 'right';
      targets.push(content);
    }

    if (targets.length === 0) return;

    targets.forEach((el) => {
      el.setAttribute('data-reveal-armed', '');
    });

    this.#observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (!entry.isIntersecting) return;

        const el = /** @type {HTMLElement} */ (entry.target);

        window.setTimeout(() => {
          el.removeAttribute('data-reveal-armed');
          el.setAttribute('data-reveal-active', '');
        }, index * STAGGER_MS);

        this.#observer?.unobserve(el);
      });
    }, OBSERVER_OPTIONS);

    targets.forEach((el) => this.#observer?.observe(el));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('reveal-on-scroll')) {
  customElements.define('reveal-on-scroll', RevealOnScroll);
}
